import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { UserCircle, MapPin, Phone, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Candidate {
  id: string;
  userId: string;
  name: string;
  email: string;
  phone: string;
  createdAt: any;
  role?: string;
  city?: string;
  specialties?: string[];
  description?: string;
}

interface CandidatesListProps {
  serviceId: string;
}

export function CandidatesList({ serviceId }: CandidatesListProps) {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const candidatesRef = collection(db, `services/${serviceId}/applicants`);
    const q = query(candidatesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const candidatesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Candidate[];
      setCandidates(candidatesData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [serviceId]);

  const handleViewProfile = (userId: string) => {
    navigate(`/prestador/${userId}`);
  };

  if (loading) {
    return <div className="text-center py-4">Carregando candidatos...</div>;
  }

  if (candidates.length === 0) {
    return <div className="text-center py-4">Ainda não há candidatos para este serviço.</div>;
  }

  return (
    <>
      <div className="space-y-4">
        {candidates.map((candidate) => (
          <div key={candidate.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <Avatar className="h-12 w-12">
              <div className="bg-blue-600 w-full h-full flex items-center justify-center text-white">
                {candidate.name?.charAt(0).toUpperCase()}
              </div>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium">{candidate.name}</h4>
                  <p className="text-sm text-gray-600">{candidate.role || 'Profissional'}</p>
                </div>
                <div className="text-sm text-gray-500">
                  {formatDistanceToNow(candidate.createdAt?.toDate() || new Date(), {
                    locale: ptBR,
                    addSuffix: true
                  })}
                </div>
              </div>
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>{candidate.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>{candidate.phone}</span>
                </div>
                {candidate.city && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{candidate.city}</span>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedCandidate(candidate)}
                >
                  Ver detalhes
                </Button>
                <Button 
                  size="sm"
                  onClick={() => handleViewProfile(candidate.userId)}
                >
                  Ver perfil completo
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Candidate Details Dialog */}
      <Dialog open={!!selectedCandidate} onOpenChange={() => setSelectedCandidate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Candidato</DialogTitle>
          </DialogHeader>
          {selectedCandidate && (
            <div className="mt-4 space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <div className="bg-blue-600 w-full h-full flex items-center justify-center text-white text-xl">
                    {selectedCandidate.name?.charAt(0).toUpperCase()}
                  </div>
                </Avatar>
                <div>
                  <h3 className="text-xl font-semibold">{selectedCandidate.name}</h3>
                  <p className="text-gray-600">{selectedCandidate.role || 'Profissional'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-gray-500" />
                    <span>{selectedCandidate.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-gray-500" />
                    <span>{selectedCandidate.phone}</span>
                  </div>
                  {selectedCandidate.city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-gray-500" />
                      <span>{selectedCandidate.city}</span>
                    </div>
                  )}
                </div>

                {selectedCandidate.specialties && selectedCandidate.specialties.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Especialidades</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCandidate.specialties.map((specialty, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {selectedCandidate.description && (
                <div>
                  <h4 className="font-medium mb-2">Sobre</h4>
                  <p className="text-gray-600">{selectedCandidate.description}</p>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={() => handleViewProfile(selectedCandidate.userId)}>
                  Ver perfil completo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}